import React from 'react';
import { Product, Variant } from 'shared/types/product';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import BundleIcon from 'components/icons/bundle-icon';
import EbookIcon from 'components/icons/ebook-icon';
import PhysicalBookIcon from 'components/icons/physical-book-icon';
import { useFormat } from 'helpers/hooks/useFormat';
import ProductPrice from './product-price';

type Props = {
  product: Product;
  onSelect: (variants?: Variant[]) => void;
  selectedReferences?: Variant[];
};

type ProductTypeConst = 'physical-book' | 'ebook' | 'bundle';

const ProductReferenceToggle = ({ product, onSelect, selectedReferences }: Props) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const productTypeKey = product.productTypeKey as ProductTypeConst;
  const expectedButtonsTypes = ['bundle', 'physical-book', 'ebook'] as const;

  const getReferences = () => expectedButtonsTypes.flatMap((type) => product[type as ProductTypeConst] ?? []);

  const getProductButton = (type: ProductTypeConst, productUrl?: string, useCurrent = true) => {
    const variants = useCurrent ? product?.variants : product[type]?.[0]?.variants;
    const label = formatProductMessage({ id: `reference.${type}` });
    const iconClassName = `-ml-8 mr-12 size-32 group-hover:fill-orange-600 transition`;

    const icon = (() => {
      switch (type) {
        case 'bundle':
          return (
            <BundleIcon
              className={`${iconClassName} ${selectedReferences === variants ? 'fill-orange-600' : 'fill-gray-400'}`}
            />
          );
        case 'ebook':
          return (
            <EbookIcon
              className={`${iconClassName} ${selectedReferences === variants ? 'fill-orange-600' : 'fill-gray-400'}`}
            />
          );
        case 'physical-book':
          return (
            <PhysicalBookIcon
              className={`${iconClassName} ${selectedReferences === variants ? 'fill-orange-600' : 'fill-gray-400'}`}
            />
          );
        default:
          return null;
      }
    })();

    return (
      <Link link={productUrl ?? ''} key={type} className="group">
        <Button
          className={`size-full ${selectedReferences === variants ? 'border-orange-500' : ''} `}
          variant="pdpButton"
        >
          {icon}
          <div className="flex flex-col items-start">
            <Typography className="font-[400] leading-loose">{label}</Typography>
            <ProductPrice variants={variants} direction="col" showDiscountBadge={false} inModalVersion={true} />
          </div>
        </Button>
      </Link>
    );
  };
  const renderButtons = () => {
    return expectedButtonsTypes
      .map((type) => {
        const referenceProduct = getReferences().find((ref) => ref.productTypeKey === type);
        const isCurrent = type === productTypeKey;
        if (Boolean(referenceProduct) || isCurrent) {
          return getProductButton(type, referenceProduct?._url, isCurrent);
        }
        return null;
      })
      .filter(Boolean);
  };

  return <div className="flex flex-col gap-12 pt-20 md:flex-row">{renderButtons()}</div>;
};

export default ProductReferenceToggle;
